import { DateTime, MsSinceEpoch, NaiveDate } from "@tento-chrono";
import type { Option } from "../core/option.ts";

/**
 * Day and column membership of a time span — lona's ONE half-open
 * convention (the operator-approved day-membership convention, 2026-09-27;
 * RFC 5545 §3.6.1 and Google Calendar: an event's end is exclusive).
 *
 * A period (a day, a column, a time-of-day window) is `[start, end)`. A
 * non-empty span belongs to every period it shares time with: an event
 * that ends at midnight belongs to the day it ends BEFORE, never to the
 * next one, and back-to-back spans do not overlap. An empty span (an
 * instant, a zero-length event) belongs to the one period containing it
 * (`start <= t < end`) — the period it starts in.
 *
 * Every membership decision of the SDK goes through here: an event's time
 * window (`EventTimeRange.Timed.isBetween`, which the calendar-list day
 * filter reads), the columns an instant or span repaints
 * (`ColumnTimepointSearch`), and every day/range query of an events index
 * (`select`: `EventsIndex` and the client-scoped `EventsTable` behind
 * `client.events.peekInRange` / `listInRange`).
 */
export namespace TimeMembership {
  /**
   * The end a membership test uses for the span `[start, end)` (ms since
   * the epoch): its own end, or — for an empty span, an instant — the next
   * millisecond, so an instant is tested as the one-ms span it starts.
   */
  export function probeEnd(start: number, end: number): number {
    return end > start ? end : start + 1;
  }

  /** Whether the span `[spanStart, spanEnd)` belongs to the period
   *  `[periodStart, periodEnd)` (all ms since the epoch). */
  export function belongsMs(
    spanStart: number,
    spanEnd: number,
    periodStart: number,
    periodEnd: number,
  ): boolean {
    return spanStart < periodEnd && periodStart < probeEnd(spanStart, spanEnd);
  }

  /** Whether `span` belongs to `period` (`belongsMs` over their instants). */
  export function belongs(span: DateTime.Range, period: DateTime.Range): boolean {
    return belongsMs(span.start.mse, span.end.mse, period.start.mse, period.end.mse);
  }

  /** A span as `select` reads it: its `[start, end)` in ms since the epoch. */
  export interface Spanned {
    /** The span, `[start, end)` in ms since the epoch. */
    readonly range: { readonly start: number; readonly end: number };
  }

  /**
   * An interval tree's root as `select` queries it (the root of an `AVLTree`
   * over `Many` nodes; `null` for an empty tree): its overlap query with both
   * ends inclusive.
   */
  export interface IntervalRoot<T extends Spanned> {
    /** Every span under the root that shares a point with `r`, both ends inclusive. */
    findAllOverlapsInclusiveEnd(r: { start: number; end: number }): T[];
  }

  /**
   * The spans under `root` that belong to the period `[start, end)` — the
   * ONE day/range query of every events index. The tree's inclusive overlap
   * query is a superset (it keeps a span that ends at `start` or starts at
   * `end`, and an instant at either bound); `belongsMs` decides. So a span
   * starting at the next midnight is not in today, one ending at today's
   * midnight is not in today, and a zero-length span at today's midnight is.
   */
  export function select<T extends Spanned>(root: Option<IntervalRoot<T>>, start: number, end: number): T[] {
    const candidates = root?.findAllOverlapsInclusiveEnd({ start, end }) ?? [];
    // Through the namespace, as every index reads it: one rule to replace
    // (`cli/tests/midnight-coverage/midnight-coverage.deno.test.ts` closes it and sees the days move).
    return candidates.filter((item) => TimeMembership.belongsMs(item.range.start, item.range.end, start, end));
  }

  /**
   * The days `range` touches, as the end-exclusive date range an all-day
   * query takes (`peekAllDayInRange`): `[the date of its start, the date of
   * its last instant + 1 day)`, in `range`'s own zone (the viewer's, for a
   * range built in it). The last instant is `end - 1 ms`, so a range ending
   * mid-day keeps that partly covered day and one ending at midnight does
   * not take the next one; an empty range touches the day of its instant
   * (`probeEnd`; the day-membership convention §7.1).
   */
  export function daysOf(range: DateTime.Range): NaiveDate.Range {
    const start = range.start.mse;
    const lastInstant = probeEnd(start, Math.max(start, range.end.mse)) - 1;
    const lastDay = DateTime.fromMse(MsSinceEpoch.of(lastInstant), range.end.tz).date;
    return new NaiveDate.Range(range.start.date, lastDay.addDays(1));
  }
}
